import styled from 'styled-components';
import { Grid } from '_utils/components/Corporate';
import { spacing } from '_utils/props';

import {
  buttonColors,
  buttonReset,
  buttonSize,
  depth,
  focusOutline,
  heading_l,
  heading_m,
  heading_xs,
  listReset,
  mq,
  safeFocusRemoval,
  svg,
  text_m,
  text_s,
  unorderedList
} from '_utils/styles';

/*
 * Search results creates its own banner-like header,
 * so we don't use the _utils/styles/section mixin
 */
export const Container = styled.section`
  ${({ theme }) => spacing({ theme, $pb: 4 })}
  background-color: ${({ theme }) => theme.colors.background.primary};

  &:last-of-type{
    ${({ theme }) => spacing({ theme, $pb: 9 })}
  }
`;

export const Header = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.element.white};
  background-color: ${({ theme }) => theme.colors.background.tertiary};
  margin: 0;
`;

export const Heading = styled.h1`
  line-height: 1;
  margin: 0;
`;

export const HeadingText = styled.span`
  ${text_m}
  display: block;
  margin: 0;
`;

export const HeadingTerm = styled.span`
  ${heading_l}
  display: block;
  margin: 0;
`;

export const Layout = styled(Grid.Row)`
  ${({ theme }) => spacing({ theme, $pt: 3 })}

  ${mq.lessThan('lg')} {
    padding-top: 0;
  }
`;

export const Column = styled(Grid.Item)`
  transition: 800ms height ease-in;
`;

export const FiltersColumn = styled(Column)`
  ${({ theme }) => spacing({ theme, $mb: 7 })}

  ${mq.lessThan('lg')} {
    display: none;

    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: ${depth.MODAL};
    background-color: ${({ theme }) => theme.colors.background.primary};

    &.filters-dialog-active {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
    }
    @media print {
      display: none;
    }
  }

  ${mq('md')} {
    ${({ theme }) => spacing({ theme, $mb: 0 })}
  }
`;

export const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const FiltersHeader = styled.div`
  background-color: ${(props) => props.theme.colors.background.white};
  display: flex;
  flex: none;
  height: 56px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 8px 16px;

  ${mq('lg')} {
    padding: 0px 0 0px 16px;
    flex-direction: row-reverse;
    height: 49px;
  }
`;

export const FiltersClear = styled.button`
  ${buttonReset()}
  ${text_s}
  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }
  color: ${({ theme }) => theme.colors.element.danger};
`;

export const FiltersTitle = styled.h2`
  ${heading_m}
  margin: 0;
`;

export const FiltersClose = styled.button`
  ${() => buttonReset()}

  position: relative;
  padding: 8px;
  display: inline-block;
  margin-right: 8px;
  border-radius: 4px;

  &:focus {
    ${({ theme }) => focusOutline({ theme })}
    .safe-focus-removal & {
      outline: 0;
    }
  }

  ${mq('lg')} {
    display: none;
  }
`;

export const CrossWrapper = styled.span`
  ${() => svg(28, 28)};
  width: 28px;
  flex: none;
  path {
    fill: ${({ theme }) => theme.colors.element.primary};
  }
`;

export const FiltersForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;

  /* comment for scrolling filters panel on desktop */
  ${mq('lg')} {
    display: block;
  }
`;

export const FiltersBody = styled.div`
  flex: 1 0 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${({ theme }) => spacing({ theme, $p: 2 })}
`;

export const FiltersFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  padding: 8px 0;

  ${mq('lg')} {
    display: none;
  }
`;

export const FiltersSubmit = styled.button`
  ${buttonReset()}
  ${({ theme }) => buttonSize(theme.buttons.default)}
  ${({ theme }) => buttonColors(theme.colors.button.primary)}
  width: 100%;
`;

export const Meta = styled.div<{$my?: any}>`
  ${text_s}
  margin: 0;
  ${({ theme }) => spacing({ theme, $py: 2 })}
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.search.border};
  min-height: 32px;
`;

export const MetaInfo = styled.div`
  ${text_s}
  margin: 0;
  flex: 1 1 auto;
`;
export const MetaControls = styled.div`
  flex: none;
  margin-left: 16px;
  @media print {
    display: none;
  }
`;

export const Bold = styled.span`
  font-weight: bold;
`;

export const ResultsList = styled.ul<{$justifyContent?: string}>`
  ${listReset}
`;

export const ResultItem = styled.li`
  position: relative;

  &:not(.no-border) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.search.border};
  }
`;

export const NoResultsContainer = styled.div`
  ${({ theme }) => spacing({ theme, $pt: 5, $pb: 7 })}
`;

export const NoResultsHeading = styled.h2`
  ${heading_m}
`;

export const NoResultsBody = styled.p`
  ${text_m}
`;

export const NoResultsSuggestions = styled.ul`
  ${({ theme }) => unorderedList({ theme })}
`;

export const FiltersButton = styled.button`
  ${({ theme }) => buttonSize(theme.buttons.default)}
  ${({ theme }) => buttonColors(theme.colors.button.secondary)}
  ${safeFocusRemoval}
  ${heading_xs}
  display: flex;
  align-items: center;

  ${mq('lg')} {
    display:none;
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.search.filtersActive};
    border-color: ${({ theme }) => theme.colors.search.filtersActive};
    color : ${({ theme }) => theme.colors.element.tertiary};
    path {
      fill: ${({ theme }) => theme.colors.element.tertiary};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.search.filtersActive};
      border-color: ${({ theme }) => theme.colors.search.filtersActive};
      color : ${({ theme }) => theme.colors.element.tertiary};
      path {
        fill: ${({ theme }) => theme.colors.element.tertiary} !important;
      }
    }
  }
`;

export const FiltersIconWrapper = styled.span`
  ${svg(24, 24)}
  width: 18px;
  flex: none;
  path {
    fill: ${({ theme }) => theme.colors.button.secondary.color};
  }

  ${FiltersButton}:hover & {
    path {
      fill: ${({ theme }) => theme.colors.button.secondary.colorHover};
    }
  }
`;

export const FiltersButtonLabel = styled.span`
  margin-left: 10px;
`;

export const FiltersButtonCount = styled.span`
  font-weight: normal;
`;

// export const PaginationContainer = styled(Grid.Row)`
//   ${({ theme }) => spacing({ theme, py:2 })}
// `
